import config from 'Config/index';
import { useAppContext } from 'Context/ReactAppContext';
import { useEffect, useState } from 'react';
import { type PlanIframeProps } from 'Components/widgets/payment-info/buy-now-pay-later/BuyNowPayLater';
import { noop } from 'lodash';
import { loadUplift } from './script';
import { buildOrder } from './UpliftForm.utils';

export const UpliftStatus = {
  OFFER_AVAILABLE: 'OFFER_AVAILABLE',
  TOKEN_AVAILABLE: 'TOKEN_AVAILABLE',
  TOKEN_RETRIEVED: 'TOKEN_RETRIEVED',
  OFFER_UNAVAILABLE: 'OFFER_UNAVAILABLE',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
};

type UpliftStatusType = $Keys<typeof UpliftStatus>;

export const useUplift = ({
  onLoaded = noop,
  onTokenAvailable = noop,
  onTokenRetrieved = noop,
  booking,
  currency,
  isReadyToDisplay,
  shouldInit = true,
  isPaymentPage = false,
}: PlanIframeProps): { shouldDisplay: boolean } => {
  const { locale, country } = useAppContext();
  const [isOfferAvailable, setIsOfferAvailable] = useState(false);
  const [isUpliftModalOpen, setIsUpliftModalOpen] = useState(false);
  const shouldDisplay = isOfferAvailable && isReadyToDisplay;

  const upliftCode = config.get(`psp.uplift.${country}.code`);
  const order = buildOrder(booking);

  const onChange = ({ status, token }: { status: UpliftStatusType }) => {
    if (status === UpliftStatus.OFFER_AVAILABLE) {
      onLoaded(true);
      setIsOfferAvailable(true);
    }
    if (status === UpliftStatus.TOKEN_AVAILABLE) {
      onTokenAvailable();
    }
    if (status === UpliftStatus.TOKEN_RETRIEVED) {
      const { card_token: cardToken } = token;
      window.Uplift.Analytics.orderSubmit(order, {
        mode_of_payment: 'pay monthly',
        payment_options: ['pay monthly'],
      });
      onTokenRetrieved(cardToken);
    }
    if (status === UpliftStatus.OFFER_UNAVAILABLE || status === UpliftStatus.SERVICE_UNAVAILABLE) {
      onLoaded(false);
    }
  };

  const isUpliftInitialized = () => !!window.Uplift;

  const configuration = {
    apiKey: config.get('psp.uplift.apiKey'),
    locale,
    currency,
    checkout: isPaymentPage,
    channel: 'desktop',
    container: '#uplift-container',
    onChange,
  };

  const initUplift = () => {
    // TO FIX: uplift info modal work only if is initialized twice on payment page
    window.Uplift.Payments.init(configuration);
    window.Uplift.Payments.load(order);
    window.Uplift.Payments.init(configuration);
    window.Uplift.Payments.load(order);
  };

  useEffect(() => {
    if (isPaymentPage && shouldDisplay) {
      window.Uplift.Payments.select();
    }
  }, [shouldDisplay, isPaymentPage]);

  useEffect(() => {
    if (!isPaymentPage && isUpliftInitialized()) {
      onLoaded(false);
      window.Uplift.Payments.load(order);
    }
  }, [order.order_amount, isPaymentPage]);

  useEffect(() => {
    if (shouldDisplay) {
      setIsUpliftModalOpen(true);
    } else if (isUpliftModalOpen) {
      window.Uplift.Payments.deselect();
      setIsUpliftModalOpen(false);
    }
  }, [shouldDisplay, isUpliftModalOpen]);

  useEffect(() => {
    if (shouldInit) {
      isUpliftInitialized() && window.Uplift.Payments.exit();
      upliftCode && loadUplift(upliftCode);
      window.upReady = initUplift;
    }
  }, [shouldInit]);

  return { shouldDisplay };
};
